/**
 * syncAuthState - Sync cookie-based auth with SecureAuthProvider's in-memory state.
 *
 * After login via RTK Query mutations (which set httpOnly cookies but don't
 * update SecureAuthProvider), call this so that DashboardBootstrap sees
 * isAuthenticated=true on subsequent client-side navigation.
 *
 * This mirrors what SecureAuthProvider.login() does internally.
 */

import { tokenService } from '../services/TokenService';
import { authStateManager } from '../services/AuthStateManager';
import { authLogger } from '@/core/utils/logger';

interface SyncUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
}

export function syncAuthState(user?: SyncUser): void {
  // Mark the token layer as cookie-authenticated so hasToken becomes true
  tokenService.setTokens({
    accessToken: null,
    expiresIn: 3600,
    tokenType: 'Bearer',
    isCookieAuth: true,
  });

  if (user) {
    authStateManager.setAuthenticatedUser({
      id: user.id,
      email: user.email,
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      organizationId: user.organizationId ?? '',
    });

    // Cache for fast reload (matches SecureAuthProvider.login pattern)
    try {
      sessionStorage.setItem(
        'auth_state_cache',
        JSON.stringify({ user, timestamp: Date.now() }),
      );
    } catch {
      // sessionStorage may be unavailable - non-fatal
    }
  }

  authLogger.info('[syncAuthState] Auth state synced after login mutation');
}
